import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { ArrowLeft, Send } from 'lucide-react'
import { Keyboard } from '@capacitor/keyboard'
import { isIOS } from '@/lib/platform'
import { Button } from '@/components/ui/button'
import { ChatInput } from '@/components/chat/ChatInput'
import { useAuthState } from '@/hooks/useAuth'
import { useLiveChat, sendLiveChatMessage } from '@/hooks/useLiveChats'
import { isLiveChatOnline, getLiveChatStatusText, getAdminProfilePicture, getAllAdminProfilePictures } from '@/lib/liveChatUtils'
import { AuthModal } from '@/components/auth/AuthModal'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { PortfolioItem } from '@/types/models'

export default function LiveChatPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthState()
  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [userInstagram, setUserInstagram] = useState<string>('')
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const { liveChat, loading } = useLiveChat(user?.uid)
  const chatIsOnline = isLiveChatOnline()
  const statusText = getLiveChatStatusText(chatIsOnline)

  // Fetch user's instagram handle
  useEffect(() => {
    const fetchUserInstagram = async () => {
      if (!user?.uid) return

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid))
        if (userDoc.exists()) {
          const userData = userDoc.data()
          setUserInstagram(userData.instagram || userData.email || 'User')
        }
      } catch (error) {
        console.error('Error fetching user instagram for live chat:', error)
        // Fallback to email if offline or error
        setUserInstagram(user.email || 'User')
      }
    }

    fetchUserInstagram()
  }, [user?.uid, user?.email])

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [liveChat?.messages])

  // iOS only — same as ChatDetailPage / AuthModal (Android system insets already move the composer).
  useEffect(() => {
    if (!isIOS()) return

    let showListener: { remove: () => Promise<void> } | undefined
    let hideListener: { remove: () => Promise<void> } | undefined

    const setup = async () => {
      showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
        setKeyboardHeight(info.keyboardHeight)
      })
      hideListener = await Keyboard.addListener('keyboardWillHide', () => {
        setKeyboardHeight(0)
      })
    }

    void setup()

    return () => {
      void showListener?.remove()
      void hideListener?.remove()
    }
  }, [])

  const handleSendMessage = useCallback(async (_artistContent?: PortfolioItem[]) => {
    if (!message.trim() || isSending) return

    if (!user) {
      setShowAuthModal(true)
      return
    }

    setIsSending(true)

    try {
      await sendLiveChatMessage(
        user.uid,
        message.trim(),
        userInstagram,
        false,
        userInstagram
      )
      setMessage('')
    } catch (error) {
      console.error('Error sending message:', error)
      alert('Failed to send message. Please try again.')
    } finally {
      setIsSending(false)
    }
  }, [message, isSending, user, userInstagram])

  const handleBackClick = () => {
    navigate(-1)
  }

  return (
    <>
      <div className="flex min-h-screen flex-col bg-background">
        {/* Same frosted header shell as ChatDetailPage (notch + md TopNav offset) */}
        <div className="sticky-header-notch-backdrop sticky top-0 z-40 shrink-0 rounded-b-2xl border-b border-border/50 bg-background/95 backdrop-blur-md md:top-[52px] dark:border-border/30 relative">
          <div className="phone-container-padded pb-3 pt-0">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackClick}
                className="h-10 w-10 shrink-0 rounded-full"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>

              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="flex shrink-0 -space-x-2">
                  {getAllAdminProfilePictures().map((admin, index) => (
                    <img
                      key={admin.name}
                      src={admin.image}
                      alt={admin.name}
                      className="h-8 w-8 rounded-full border-2 border-white object-cover"
                      style={{ zIndex: 3 - index }}
                      title={admin.name}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(admin.name)}&background=000&color=fff&size=128&length=1&bold=true`
                      }}
                    />
                  ))}
                </div>

                <div className="min-w-0">
                  <h1 className="truncate text-base font-medium">{t('liveChat.title')}</h1>
                  <div className="flex items-center gap-1.5">
                    <div
                      className={`h-2 w-2 shrink-0 rounded-full ${chatIsOnline ? 'bg-green-500' : 'bg-muted-foreground'}`}
                    />
                    <p className="truncate text-xs text-muted-foreground">{statusText}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Same scroll + bottom padding pattern as ChatDetailPage StickToBottom */}
        <div
          className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto [-webkit-overflow-scrolling:touch]"
          style={{
            paddingBottom: keyboardHeight > 0 ? 'calc(44px + 8px)' : 'calc(44px + 8px + 2rem)',
            transition: 'padding-bottom 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          }}
        >
          {/* Match ChatMessagesContainer: phone-container-padded + py-4 */}
          <div className="flex min-h-full flex-col bg-background py-4 phone-container-padded">
            {/* Reply-time note — lives in thread like a banner message, not squeezed under the header */}
            <div className="mb-4 flex justify-center">
              <div className="border border-border/50 bg-muted/50 px-4 py-3 text-center dark:border-border/30 rounded-[18px] max-w-xl w-full shadow-sm">
                <p className="break-words text-sm leading-relaxed text-muted-foreground [&_strong]:font-medium [&_strong]:text-foreground">
                  <Trans i18nKey="liveChat.infoText" components={{ strong: <strong />, br: <br /> }}>
                    Chat with <strong>Lotta</strong>, <strong>Keijo</strong> or <strong>Jari</strong>.<br /> We reply within 3 minutes!
                  </Trans>
                </p>
              </div>
            </div>

            {loading ? (
              <div className="flex flex-1 items-center justify-center py-12">
                <div className="text-center">
                  <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  <p className="text-sm text-muted-foreground">
                    {t('liveChat.loadingMessages', 'Loading messages...')}
                  </p>
                </div>
              </div>
            ) : liveChat?.messages && liveChat.messages.length > 0 ? (
              <div className="space-y-2">
                {liveChat.messages.map((messageItem, index) => {
                  const isOwnMessage = !messageItem.isAdmin
                  const showAvatar =
                    messageItem.isAdmin &&
                    (index === liveChat.messages.length - 1 ||
                      liveChat.messages[index + 1]?.isAdmin !== messageItem.isAdmin)

                  return (
                    <div
                      key={index}
                      className={`flex w-full ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`flex max-w-[85%] items-end gap-2 ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}
                      >
                        {!isOwnMessage && (
                          <div className="w-8 shrink-0">
                            {showAvatar && (
                              <img
                                src={getAdminProfilePicture(messageItem.senderUsername)}
                                alt={messageItem.senderUsername}
                                className="h-8 w-8 rounded-full object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement
                                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(messageItem.senderUsername)}&background=000&color=fff&size=128&length=1&bold=true`
                                }}
                              />
                            )}
                          </div>
                        )}

                        <div className={`flex min-w-0 flex-col ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                          {messageItem.isAdmin && showAvatar && (
                            <p className="mb-1 ml-1 text-xs font-medium text-muted-foreground">
                              {messageItem.senderUsername}
                            </p>
                          )}

                          <div
                            className={`${
                              isOwnMessage
                                ? 'rounded-[18px] rounded-br-[4px] bg-primary text-primary-foreground'
                                : 'rounded-[18px] rounded-bl-[4px] bg-muted text-foreground'
                            } px-4 py-3 shadow-sm`}
                          >
                            <p className="text-[15px] leading-[20px] break-words whitespace-pre-wrap">
                              {messageItem.content}
                            </p>
                          </div>

                          <p
                            className={`mt-1 text-[10px] text-muted-foreground ${isOwnMessage ? 'mr-1' : 'ml-1'}`}
                          >
                            {messageItem.timestamp.toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center py-12">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                  <Send className="h-7 w-7 text-muted-foreground" />
                </div>
                <p className="text-center text-sm text-muted-foreground">
                  {t('liveChat.noMessages', 'No messages yet. Say hi!')}
                </p>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area — frosted bar (ChatDetailPage) */}
        <div
          className="pointer-events-auto fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl border-t border-border/50 bg-background/95 backdrop-blur-md dark:border-border/30"
          style={{
            transform: keyboardHeight > 0 ? `translateY(-${keyboardHeight}px)` : 'translateY(0)',
            transition: 'transform 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          }}
        >
          <div className="phone-container-padded">
            {!user && (
              <div className="px-4 pt-3">
                <div className="mb-0 rounded-[var(--card-radius)] bg-muted p-3 text-center">
                  <p className="text-sm text-muted-foreground">{t('liveChat.pleaseSignIn', 'Please sign in to send messages')}</p>
                  <button
                    type="button"
                    onClick={() => setShowAuthModal(true)}
                    className="mt-1 text-sm font-medium text-primary underline"
                  >
                    {t('auth.login', 'Sign In')}
                  </button>
                </div>
              </div>
            )}
            <ChatInput
              message={message}
              setMessage={setMessage}
              pendingImages={[]}
              onSendMessage={handleSendMessage}
              onImageSelect={() => {}}
              onRemoveImage={() => {}}
              disabled={!user || isSending}
              autoFocusComposer={!showAuthModal && !!user}
              keyboardVisible={keyboardHeight > 0}
              attachmentsDisabled
            />
          </div>
        </div>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onOpenChange={setShowAuthModal}
        dismissible={true}
        defaultMode="login"
        source="live_chat"
      />
    </>
  )
}
